import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { ZeroProvider } from "@rocicorp/zero/react";
import type { UpdateNeededReason } from "@rocicorp/zero";
import { toast } from "sonner";
import { LoadingIcon } from "@/components/ui/loading-icon";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ZeroAuthRecovery } from "@/components/app/zero-auth-recovery";
import { ZeroErrorRecovery } from "@/components/app/zero-error-recovery";
import { ZeroVisibilityRefresh } from "@/lib/zero-visibility-refresh";
import { completePlatformSignOut } from "@/lib/logout";
import { isElectronRuntime, isBuiltDesktopApp } from "@/lib/logout";
import { selectCacheURL } from "@/lib/select-cache-url";
import { useAccessToken } from "@/hooks/use-access-token";
import {
  zeroSessionAuthFlag,
  useZeroSessionToken,
} from "@/hooks/use-zero-session-token";
import { useElectronUpdater } from "@/hooks/use-electron-updater";
import { schema } from "@/lib/zero-schema";
import { mutators } from "@/lib/mutators";
import { trackAuthIntent } from "@/lib/analytics-events";
import { deriveAuthIntentContext } from "./auth-intent-context";
import {
  getActiveSyncUrl,
  setHomeRegionForZero,
  subscribeActiveSyncUrl,
} from "@/lib/active-region";
import { resolveHomeRegion } from "@/lib/region-resolver";
import { PublicWorkspaceAnonymousView } from "@/components/workspace/PublicWorkspaceAnonymousView";
import { PublicQuestionView } from "@/components/workspace/PublicQuestionView";
import { useGithubSignup } from "@/components/app/use-github-signup";
import { useAnonymousLanding } from "./use-anonymous-landing";
import { useLibraryPrefetch } from "./use-library-prefetch";
import { useElectronMinVersionGate } from "./use-electron-min-version-gate";
import { useCallbackAutoRetry } from "./use-callback-auto-retry";
import { useGuestInvite } from "./use-guest-invite";
import { hideBootSplash } from "@/lib/boot-splash";
import type { UpdateRequiredReason } from "@/components/UpdateRequiredDialog";
import {
  GuestMode,
  InviteLoginPage,
  UpdateRequiredDialog,
} from "./lazy-imports";
import {
  AuthLoadingScreen,
  AuthLoadingFullscreen,
  AuthTimeoutScreen,
  SessionRecoveryScreen,
  SignInScreen,
} from "./auth-screens";

const explicitCacheUrl = import.meta.env.VITE_ZERO_CACHE_URL as
  | string
  | undefined;

interface AuthGateProps {
  children: React.ReactNode;
}

/**
 * Outer auth-gating shell — owns the AuthKit-driven sign-in/up flow,
 * the boot splash, the Electron min-version + Zero `onUpdateNeeded`
 * dialogs, and the `<ZeroProvider>` mount itself.
 *
 * Children render inside `ZeroProvider` only after we have a valid auth
 * token; before that they're never mounted, so no Zero-aware code
 * downstream runs.
 */
export function AuthGate({ children }: AuthGateProps) {
  const { user, isLoading, signIn, signUp, signOut, getSignUpUrl } = useAuth();
  const { token: authToken, resolved: authTokenResolved } = useAccessToken();
  const { token: zeroSessionToken, refreshToken: refreshZeroSessionToken } =
    useZeroSessionToken(authToken, authTokenResolved);

  // When the Zero session token path is enabled, prefer the Zero JWT (12h
  // TTL, decoupled from WorkOS access token expiry). The hook's `useState`
  // initializer runs `loadStoredZeroToken()` synchronously on the first
  // render, so `zeroSessionToken` is non-null on render #1 whenever we have
  // a valid stored JWT — meaning we should use it immediately rather than
  // gating on the hook's async `resolved` flag. The previous gate created a
  // race window where Zero would mount with the WorkOS token (causing
  // transient PushFailed 401 errors) until the async mint flipped `resolved`
  // to true. Cold-start case (no stored token): falls back to the WorkOS
  // token until the hook mints a fresh JWT — same brief race window as
  // before, but only affects fresh sign-ins (not page reloads with a valid
  // stored JWT). When the flag is disabled, always use the WorkOS token
  // directly (Tier 1 behavior from PR #765).
  const effectiveZeroAuthToken =
    zeroSessionAuthFlag.enabled && zeroSessionToken
      ? zeroSessionToken
      : authToken;

  // Stabilize the ZeroProvider non-auth props so re-renders from
  // useZeroSessionToken (setToken / setResolved) don't churn ZeroProvider's
  // `keysWithoutAuth` deps and cause the Zero instance to be closed and
  // recreated mid-connection. Before this, initial sign-in with the session
  // token flag on produced 5-7 Zero instances in rapid succession, cascading
  // "Zero was explicitly closed" / "Store is closed" errors.
  const zeroAuthContext = useMemo(() => ({ sub: user?.id ?? "" }), [user?.id]);

  // Option D (2026-05-19): single-region cutover makes `cacheURL`
  // fully derivable from `home_region`. The active-region store
  // exposes ONE value (the sync URL for the current home_region) and
  // changes only when home_region resolves at sign-in — never on
  // workspace switches. Pre-Option D this subscription churned on
  // every workspace cursor move, re-keying ZeroProvider and
  // unmounting <AuthenticatedApp>, which destroyed in-React refs.
  const regionalSyncUrl = useSyncExternalStore(
    subscribeActiveSyncUrl,
    getActiveSyncUrl,
    getActiveSyncUrl,
  );
  // v0.1.44 — built desktop apps ALWAYS use the regional resolver's
  // syncUrl. `VITE_ZERO_CACHE_URL` is a dev-only escape hatch.
  //
  // The previous precedence (`explicitCacheUrl || regional`) was the
  // load-bearing root cause of the v0.1.39–v0.1.43 multi-region bugs:
  // Doppler set `VITE_ZERO_CACHE_URL=https://sync.ano.dev/` at build
  // time, which made ZeroProvider unconditionally connect to the
  // CF-routed apex (api-us) regardless of the active workspace's
  // region. The region resolver's cache + store updates were correct
  // but the value never reached ZeroProvider — explicit env always
  // won. Confirmed via runtime probe: `window.__zero.server` =
  // "https://sync.ano.dev/" (trailing slash = env, not resolver).
  //
  // Selection logic is extracted into `selectCacheURL` so the rule
  // is unit-tested and can't silently revert.
  const cacheURL = selectCacheURL({
    isBuiltApp: isBuiltDesktopApp(),
    regionalSyncUrl,
    explicitCacheUrl,
  });

  // Resolve the user's home_region once after sign-in. Post-Option F.3
  // (2026-05-20) this is cache-first → synchronous client-side timezone
  // detection — no network. The localStorage cache is populated on
  // first resolution and reused on subsequent sign-ins.
  //
  // The cache is wiped on sign-out (see `logout.ts`) so a traveler
  // signing out + in picks up the new region via timezone detection.
  // Mid-session region changes are NOT supported: pre-v0.1.58 this was
  // enforced by Option D ("collapse desktop region machinery to
  // home_region only"). v0.1.58 reintroduced reactive switching via
  // `HomeRegionWatcher`, which violated a Zero invariant (cookie +
  // clientGroupID are bound to the `cacheURL`'s zero-cache backend,
  // not portable across replicas — the JS Zero client uses the same
  // IDB store regardless of `cacheURL`, so flipping the URL mid-
  // session left the new replica with the previous replica's stale
  // identity and triggered an infinite ZeroProvider remount loop in
  // prod). HomeRegionWatcher was removed and Option D's static
  // semantics restored.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    void resolveHomeRegion().then((region) => {
      if (cancelled) return;
      setHomeRegionForZero(region);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Discover integrations preload: fire once shortly after we have a
  // signed-in user + access token so the Library dialog renders instantly
  // when the user opens it.
  useLibraryPrefetch({ authToken, userId: user?.id });

  // requiresUpdate is a single state with first-setter-wins semantics — both
  // the proactive min-version fetch and the reactive onUpdateNeeded callback
  // may write it, and we don't want a later writer to clobber the earlier one.
  const { state: electronUpdaterState } = useElectronUpdater();
  const [requiresUpdate, setRequiresUpdate] =
    useState<UpdateRequiredReason | null>(null);

  const handleZeroUpdateNeeded = useCallback((reason: UpdateNeededReason) => {
    // All three reason.type variants (NewClientGroup / VersionNotSupported /
    // SchemaVersionNotSupported) mean the client is incompatible with the
    // server — handle them identically.
    const reactiveMessage = reason.message ?? reason.type;

    if (isElectronRuntime()) {
      // reload() on Electron reloads the same packaged bundle — infinite loop.
      // Setting requiresUpdate triggers AppInner's exclusive render which
      // unmounts ZeroProvider and tears down the WebSocket.
      setRequiresUpdate((prev) =>
        prev ? prev : { kind: "reactive", message: reactiveMessage },
      );
      return;
    }

    // Web: reload pulls a fresh bundle from the CDN.
    toast.error(
      `App update needed: ${reason.type}${reason.message ? ` — ${reason.message}` : ""}. Reloading...`,
      { duration: 5000 },
    );
    setTimeout(() => window.location.reload(), 5000);
  }, []);

  // Proactive min-version check: runs once per auth session, Electron-only.
  // Fails open on network error. (See use-electron-min-version-gate.ts.)
  useElectronMinVersionGate({
    userId: user?.id,
    electronUpdaterState,
    setRequiresUpdate,
  });

  // Sign-out cleanup: derived instead of synced so we don't need an effect
  // that calls setState (which triggers cascading renders). When the user
  // signs out, we simply don't render the dialog; the underlying state can
  // stay populated harmlessly until the next sign-in resets it via the
  // effect above.
  const activeRequiresUpdate = user?.id ? requiresUpdate : null;

  const [authAction, setAuthAction] = useState<"signin" | "signup" | null>(
    null,
  );
  const [authActionError, setAuthActionError] = useState<string | null>(null);
  const [authLoadingTimedOut, setAuthLoadingTimedOut] = useState(false);
  const isAuthActionPending = authAction !== null;

  // Remove the static index.html splash only after React has painted at least
  // one frame, so startup never flashes an uninitialized shell.
  useEffect(() => {
    const frame1 = requestAnimationFrame(() => {
      requestAnimationFrame(hideBootSplash);
    });
    return () => cancelAnimationFrame(frame1);
  }, []);

  const handleSignOut = () => {
    void completePlatformSignOut(signOut);
  };

  const handleSignIn = async (loginHint?: string) => {
    if (authAction === "signin") return;
    setAuthActionError(null);
    setAuthAction("signin");
    trackAuthIntent({ action: "signin", ...deriveAuthIntentContext() });
    try {
      const returnTo = window.location.href;
      sessionStorage.setItem("ano:auth-return-url", returnTo);
      await signIn({ loginHint, state: { returnTo } });
    } catch (error) {
      console.error("Failed to start sign in:", error);
      setAuthActionError("Could not start sign-in. Please try again.");
      setAuthAction(null);
    }
  };

  const handleSignUp = async (loginHint?: string) => {
    if (authAction === "signup") return;
    setAuthActionError(null);
    setAuthAction("signup");
    trackAuthIntent({ action: "signup", ...deriveAuthIntentContext() });
    try {
      const returnTo = window.location.href;
      sessionStorage.setItem("ano:auth-return-url", returnTo);
      await signUp({ loginHint, state: { returnTo } });
    } catch (error) {
      console.error("Failed to start sign up:", error);
      setAuthActionError("Could not start sign-up. Please try again.");
      setAuthAction(null);
    }
  };

  // Lurker "Continue with GitHub" one-click signup (#1925). Kept from staging.
  const handleContinueWithGithub = useGithubSignup(getSignUpUrl, signUp, () =>
    trackAuthIntent({ action: "signup", ...deriveAuthIntentContext() }),
  );

  // Auto-retry the OAuth callback once (throttled) when the user lands on
  // `/callback` but the session never resolved. (See use-callback-auto-retry.ts.)
  useCallbackAutoRetry({
    user,
    isLoading,
    isAuthActionPending,
    signIn,
    onError: setAuthActionError,
  });

  // Show "taking too long" screen after 12s of loading
  useEffect(() => {
    if (!isLoading || authLoadingTimedOut) return;
    const timer = setTimeout(() => setAuthLoadingTimedOut(true), 12000);
    return () => clearTimeout(timer);
  }, [isLoading, authLoadingTimedOut]);

  // Guest DM access — parse invite token for unauthenticated guest mode and
  // persist it across OAuth redirects. Placed before early returns to satisfy
  // React hooks rules. (See use-guest-invite.ts.)
  const { guestInviteToken, isGuestEligible } = useGuestInvite();

  // Phase 5 — anonymous public-workspace view. When a logged-out visitor
  // lands on a workspace subdomain whose owner enabled `anonymous_view`, the
  // `if (!user)` branch below renders a read-only community surface instead of
  // the sign-in wall. The lookup is gated on auth having resolved to "no user".
  const anonymousLanding = useAnonymousLanding(!user && !isLoading);

  if (isLoading && !authLoadingTimedOut) {
    return <AuthLoadingScreen />;
  }

  if (isLoading && authLoadingTimedOut) {
    return (
      <AuthTimeoutScreen
        title="Authentication is taking too long"
        description="The auth provider did not finish initialization."
        onSignOut={handleSignOut}
      />
    );
  }

  if (!user && guestInviteToken && isGuestEligible) {
    return (
      <Suspense
        fallback={
          <div className="flex h-screen items-center justify-center bg-background">
            <LoadingIcon size={24} className="text-foreground" />
          </div>
        }
      >
        <GuestMode
          token={guestInviteToken}
          onSignIn={() => void handleSignIn()}
          onSignUp={(hint) => void handleSignUp(hint)}
        />
      </Suspense>
    );
  }

  if (!user && guestInviteToken && !isGuestEligible) {
    return (
      <Suspense
        fallback={
          <div className="flex h-screen items-center justify-center bg-background">
            <LoadingIcon size={24} className="text-foreground" />
          </div>
        }
      >
        <InviteLoginPage
          token={guestInviteToken}
          onSignIn={(hint) => void handleSignIn(hint)}
          onSignUp={(hint) => void handleSignUp(hint)}
          authAction={authAction}
          authActionError={authActionError}
        />
      </Suspense>
    );
  }

  if (!user) {
    // Phase 5: anonymous public-workspace view, when the owner enabled it.
    // While the metadata lookup is in flight, hold on a full-screen loader
    // so we don't flash the sign-in wall before deciding.
    if (anonymousLanding.slug) {
      if (anonymousLanding.status !== "ready") {
        return <AuthLoadingFullscreen />;
      }
      if (anonymousLanding.meta?.anonymous_view) {
        // Anchored deep link (`…/c/<channel>/m/<id>`) → focused single-question
        // surface; every other public landing → the community view.
        if (anonymousLanding.messageId && anonymousLanding.channelName) {
          return (
            <PublicQuestionView
              meta={anonymousLanding.meta}
              channelName={anonymousLanding.channelName}
              messageId={anonymousLanding.messageId}
              onSignIn={() => void handleSignIn()}
              onSignUp={() => void handleSignUp()}
              onContinueWithGithub={() => void handleContinueWithGithub()}
            />
          );
        }
        return (
          <PublicWorkspaceAnonymousView
            meta={anonymousLanding.meta}
            onSignIn={() => void handleSignIn()}
            onSignUp={() => void handleSignUp()}
            onContinueWithGithub={() => void handleContinueWithGithub()}
          />
        );
      }
    }
    return (
      <SignInScreen
        authAction={authAction}
        authActionError={authActionError}
        onSignIn={() => void handleSignIn()}
        onSignUp={() => void handleSignUp()}
      />
    );
  }

  if (!authTokenResolved) {
    return <AuthLoadingFullscreen />;
  }

  if (!authToken) {
    return (
      <SessionRecoveryScreen
        authAction={authAction}
        isAuthActionPending={isAuthActionPending}
        onSignIn={() => void handleSignIn()}
        onSignOut={handleSignOut}
      />
    );
  }

  // Exclusive render: when the blocking update dialog is up, unmount
  // <ZeroProvider> entirely. This is what actually stops Zero from
  // reconnecting in the background — the `closeZero()` helper in zero.ts
  // is dead code against ZeroProvider's internal instance, so unmounting
  // is the only reliable way to halt the WebSocket. Users deep in the tree
  // that call useZero() won't render at all because AuthenticatedApp is
  // the sibling branch.
  if (activeRequiresUpdate) {
    return (
      <Suspense fallback={null}>
        <UpdateRequiredDialog reason={activeRequiresUpdate} />
      </Suspense>
    );
  }

  return (
    <ErrorBoundary>
      <ZeroProvider
        // Key on the actual cacheURL so the Zero client only remounts
        // when the resolved sync host changes. Using `regionalUrls.region`
        // here would cause unnecessary remounts in dev / when
        // VITE_ZERO_CACHE_URL is set, where cacheURL is invariant under
        // workspace switches.
        key={`${user.id}:${cacheURL}`}
        userID={user.id}
        context={zeroAuthContext}
        schema={schema}
        cacheURL={cacheURL}
        mutators={mutators}
        auth={effectiveZeroAuthToken}
        // Default is 5 minutes — too aggressive for a chat app where users
        // frequently background the window for short stretches. Each
        // disconnect/reconnect is a chance to hit the auth-refresh race.
        hiddenTabDisconnectDelay={30 * 60 * 1000}
        onUpdateNeeded={handleZeroUpdateNeeded}
      >
        <ZeroAuthRecovery refreshZeroSessionToken={refreshZeroSessionToken} />
        <ZeroErrorRecovery />
        <ZeroVisibilityRefresh
          refreshZeroSessionToken={refreshZeroSessionToken}
        />
        {children}
      </ZeroProvider>
    </ErrorBoundary>
  );
}
